import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import "../PickerModal/styles.scss"; // reuse .picker-modal-*, .unit-toggle, .toggle-indicator
import "./styles.scss";

type ToggleOption = { value: string; label: string };

/**
 * Render-prop API handed to the input body. `hasError` drives the red input
 * styling, `clearError` is called from the input's onChange (typing dismisses a
 * stale error), and `submit` lets the input trigger Ok on Enter (desktop / hw
 * keyboard) so behaviour matches the footer button.
 */
export interface TextInputBodyApi {
  hasError: boolean;
  clearError: () => void;
  submit: () => void;
}

interface TextInputModalProps {
  open: boolean;
  /** Commit + close — the parent's closeAge/Height/WeightModal, unchanged. */
  onClose: () => void;
  /** Dismiss without committing — the parent's cancel handler. */
  onCancel: () => void;
  title: string; // "Enter age" | "Enter height" | "Enter weight"
  /** Return null when valid, or the error string ("Invalid age input") when not. */
  validate: () => string | null;
  children: (api: TextInputBodyApi) => React.ReactNode;
  toggleUnits?: {
    value: string;
    onChange: (value: string) => void;
    options: ToggleOption[];
  };
}

const TextInputModal: React.FC<TextInputModalProps> = ({
  open,
  onClose,
  onCancel,
  title,
  validate,
  children,
  toggleUnits,
}) => {
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleOk = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content
          ref={contentRef}
          className="modal-content"
          style={{ touchAction: "auto" }}
          onOpenAutoFocus={(e) => {
            // Focus the first numeric field ourselves (instead of Radix's default
            // first-focusable, which is Cancel) so the native keypad + caret show.
            e.preventDefault();
            contentRef.current
              ?.querySelector<HTMLInputElement>("input.text-input-el")
              ?.focus();
          }}
        >
          <Dialog.Title className="picker-modal-title h3">{title}</Dialog.Title>

          <div className="text-input-modal-body">
            {toggleUnits && (
              /** TODO: replace MUI Toggle with Radix UI as we're removing MUI */
              <ToggleButtonGroup
                value={toggleUnits.value}
                exclusive
                onChange={(_, value) => {
                  if (value && toggleUnits?.onChange) {
                    toggleUnits.onChange(value);
                    setError(null);
                  }
                }}
                aria-label="unit toggle"
                className="unit-toggle"
              >
                {toggleUnits.options.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    className="unit-toggle-button"
                    disableRipple
                  >
                    {option.label}
                  </ToggleButton>
                ))}
                <span className="toggle-indicator"></span>
              </ToggleButtonGroup>
            )}

            {children({
              hasError: !!error,
              clearError: () => setError(null),
              submit: handleOk,
            })}

            {/* Always rendered (reserves height) so validation text never resizes
                the card. */}
            <p className="text-input-error p3">{error ?? " "}</p>
          </div>

          <div className="picker-modal-footer">
            <button className="text-button" onClick={onCancel}>
              Cancel
            </button>
            <button className="primary-button" onClick={handleOk}>
              Ok
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TextInputModal;
